export type FilterOption = {
  value: string;
  label: string;
};

const isFilterOption = (
  item: unknown
): item is { value: string; label?: unknown } =>
  typeof item === "object" &&
  item !== null &&
  typeof (item as { value?: unknown }).value === "string";

/**
 * Safely decode a persisted multi-select filter selection.
 *
 * The value is a JSON-encoded array of `{ value, label }` options, but it can
 * arrive corrupted or hand-edited — filter state is sourced from both
 * localStorage and URL search params — so this never throws: malformed JSON or
 * an unexpected top-level shape yields `[]`, and individual entries without a
 * string `value` are dropped. A missing/invalid label falls back to the value
 * so a valid selection is always preserved.
 */
export const decodeFilterOptions = (
  raw: string | null | undefined
): Array<FilterOption> => {
  if (!raw) return [];

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }

  if (!Array.isArray(parsed)) return [];

  return parsed.filter(isFilterOption).map((item) => ({
    value: item.value,
    label: typeof item.label === "string" ? item.label : item.value,
  }));
};
